import type { ReactElement } from "react";
import { useEffect, useMemo, useState } from "react";

import { useTranslate } from "@similarweb/i18n-provider";
import { SegmentedControl, type SegmentedControlValue } from "@similarweb/ui-components";

import type { ChipGroupItem } from "app/pages/sales-intelligence/common-components/filters-panel/components/ChipsGroup/ChipsGroup";
import ChipsGroup from "app/pages/sales-intelligence/common-components/filters-panel/components/ChipsGroup/ChipsGroup";
import { FilterConditionDialog } from "app/pages/sales-intelligence/common-components/filters-panel/components/FilterConditionDialog/FilterConditionDialog";
import I18n from "app/pages/sales-intelligence/common-components/i18n/I18n";
import {
    INDUSTRIES_CODE_NAMES,
    type IndustryCodeDto,
    industryCodeDtoMap,
    type IndustryCodes,
} from "app/pages/sales-intelligence/domain/industry/types/codes";
import type { IIndustryCodesFilterDto } from "app/pages/sales-intelligence/domain/industry/types/filter-values";
import { hasSameIndustryCodesObject } from "app/pages/sales-intelligence/domain/industry/utils/operations";
import InclusionTechnologyRadioButtons from "app/pages/sales-intelligence/modules/technology/components/InclusionTechnologyRadioButtons/InclusionTechnologyRadioButtons";
import {
    type InclusionOption,
    inclusionOptionMap,
    operatorOptionMap,
} from "app/pages/sales-intelligence/types/commonMaps";

import { IndustryCodesFilterValue } from "../../../../../models/IndustryCodesFilter/IndustryCodesFilterValue";
import industryCodesFilterTrackingService from "../../../../../services/tracking/industry-codes-filter/trackingService";
import { getUnavailableIndustryCodes } from "../../../../../utils/getUnavailableInclusionFilterValues";
import { hasDuplicateInclusionCondition } from "../../../../../utils/hasDuplicateInclusionCondition";
import leadsSearchFiltersI18nKeys from "../../../../../utils/i18n";
import { AutocompleteCodes } from "../../AutocompleteCodes/AutocompleteCodes";
import { CodesDropdown } from "../../CodesDropdown/CodesDropdown";
import IndustryCodesBulkInput from "../../IndustryCodesBulkInput/IndustryCodesBulkInput";
import {
    MAX_INDUSTRY_BULK_VALUES,
    mergeIndustryBulkCodes,
    parseIndustryBulkValue,
} from "../../IndustryCodesBulkInput/utils";
import { StyledDropdownsFlexContainer } from "../../styles";

import {
    StyledBulkModeContainer,
    StyledCodesInputSection,
    StyledInputModeFrame,
    StyledModalChipsSection,
    StyledModalContent,
    StyledSegmentedControlContainer,
} from "./styles";

const INIT_INDUSTRY_CODES_MODAL_VALUE: IIndustryCodesFilterDto = {
    inclusion: inclusionOptionMap.includeOnly,
    sicCodes: [],
    naicsCodes: [],
};

const INPUT_MODE_SINGLE_INDEX = 0;
const INPUT_MODE_BULK_INDEX = 1;

type IndustryCodesChipType = `${IndustryCodeDto}:${string}`;

const getIndustryCodesChipsData = (item: IIndustryCodesFilterDto): ChipGroupItem<IndustryCodesChipType>[] => [
    ...item.sicCodes.map((code) => ({
        name: `${INDUSTRIES_CODE_NAMES[industryCodeDtoMap.sic]}: ${code}`,
        type: `${industryCodeDtoMap.sic}:${code}` as IndustryCodesChipType,
    })),
    ...item.naicsCodes.map((code) => ({
        name: `${INDUSTRIES_CODE_NAMES[industryCodeDtoMap.naics]}: ${code}`,
        type: `${industryCodeDtoMap.naics}:${code}` as IndustryCodesChipType,
    })),
];

interface IIndustryCodesModalProps {
    isOpen: boolean;
    selected: IIndustryCodesFilterDto | null;
    value: IIndustryCodesFilterDto[] | null;
    codeOptions: readonly IndustryCodeDto[];
    naicsCodes: readonly IndustryCodes[];
    sicCodes: readonly IndustryCodes[];
    isExcludeAvailable: boolean;
    onClose: () => void;
    onCancel: () => void;
    onApply: (item: IIndustryCodesFilterDto, selected?: IIndustryCodesFilterDto | null) => void;
}

export const IndustryCodesModal = ({
    isOpen,
    selected,
    value,
    codeOptions,
    naicsCodes,
    sicCodes,
    isExcludeAvailable,
    onClose,
    onCancel,
    onApply,
}: IIndustryCodesModalProps): ReactElement => {
    const { translate } = useTranslate();
    const [industryCodesItem, setIndustryCodesItem] = useState<IIndustryCodesFilterDto>(
        INIT_INDUSTRY_CODES_MODAL_VALUE,
    );
    const [codeType, setCodeType] = useState<IndustryCodeDto>(industryCodeDtoMap.naics);
    const [inputModeIndex, setInputModeIndex] = useState(INPUT_MODE_SINGLE_INDEX);
    const [bulkRawValue, setBulkRawValue] = useState("");

    const filterValue = useMemo(() => new IndustryCodesFilterValue(industryCodesItem), [industryCodesItem]);

    const codesFullList = {
        [industryCodeDtoMap.naics]: naicsCodes,
        [industryCodeDtoMap.sic]: sicCodes,
    };

    const inputModeSegmentValues = useMemo(
        (): SegmentedControlValue[] => [
            {
                value: "single",
                label: translate(leadsSearchFiltersI18nKeys.industryFilterCodesInputModeSingle()),
            },
            {
                value: "bulk",
                label: translate(leadsSearchFiltersI18nKeys.industryFilterCodesInputModeBulk()),
            },
        ],
        [translate],
    );

    const initValue = () => {
        setIndustryCodesItem(INIT_INDUSTRY_CODES_MODAL_VALUE);
        setCodeType(industryCodeDtoMap.naics);
        setInputModeIndex(INPUT_MODE_SINGLE_INDEX);
        setBulkRawValue("");
    };

    const handleSetInclusion = (inclusionOption: InclusionOption) => {
        setIndustryCodesItem((prev) => ({
            ...prev,
            inclusion: inclusionOption,
        }));
    };

    const handleCancel = () => {
        onCancel();
        initValue();
    };

    const getItemWithBulkCodes = (item: IIndustryCodesFilterDto): IIndustryCodesFilterDto => {
        if (!bulkRawValue.trim()) {
            return item;
        }

        return mergeIndustryBulkCodes({
            item,
            bulkRawValue,
            codeType,
            naicsCodes,
            sicCodes,
            excludedCodes: getUnavailableIndustryCodes(value ?? [], selected, codeType),
        });
    };

    const handleOnApply = () => {
        const itemToApply =
            inputModeIndex === INPUT_MODE_BULK_INDEX ? getItemWithBulkCodes(industryCodesItem) : industryCodesItem;

        if (inputModeIndex === INPUT_MODE_BULK_INDEX && bulkRawValue.trim()) {
            industryCodesFilterTrackingService.trackIndustryCodesBulkAddModalButtonClick(
                INDUSTRIES_CODE_NAMES[codeType],
                "add",
            );
        }

        onApply(itemToApply, selected);
        initValue();
    };

    const handleCodeUpdate = (codes: readonly string[], isSelected: boolean) => {
        setIndustryCodesItem((prev) =>
            new IndustryCodesFilterValue(prev).with({
                [codeType]: codes,
            }),
        );

        industryCodesFilterTrackingService.trackingIndustryCodesChange(
            INDUSTRIES_CODE_NAMES[codeType],
            isSelected ? "unselect" : "select",
        );
    };

    const handleChipDelete = (chip: ChipGroupItem<IndustryCodesChipType>) => {
        const [codesKey, code] = chip.type.split(":") as [IndustryCodeDto, string];

        setIndustryCodesItem((prev) =>
            new IndustryCodesFilterValue(prev).with({
                [codesKey]: prev[codesKey].filter((itemCode) => itemCode !== code),
            }),
        );
    };

    const handleInputModeChange = (nextIndex: number) => {
        if (nextIndex === INPUT_MODE_BULK_INDEX) {
            industryCodesFilterTrackingService.trackIndustryCodesBulkAddClick(INDUSTRIES_CODE_NAMES[codeType]);
            setInputModeIndex(nextIndex);
            return;
        }

        if (inputModeIndex === INPUT_MODE_BULK_INDEX && bulkRawValue.trim()) {
            setIndustryCodesItem((prev) => getItemWithBulkCodes(prev));
            setBulkRawValue("");
        }

        setInputModeIndex(nextIndex);
    };

    const isApplyDisabled = () => {
        const itemToValidate =
            inputModeIndex === INPUT_MODE_BULK_INDEX ? getItemWithBulkCodes(industryCodesItem) : industryCodesItem;
        const { sicCodes: sic, naicsCodes: naics } = itemToValidate;

        const conditions = value ?? [];

        const isBulkOverLimit =
            inputModeIndex === INPUT_MODE_BULK_INDEX &&
            parseIndustryBulkValue(bulkRawValue).length > MAX_INDUSTRY_BULK_VALUES;

        return (
            (sic.length === 0 && naics.length === 0) ||
            hasSameIndustryCodesObject(itemToValidate, selected) ||
            hasDuplicateInclusionCondition(conditions, itemToValidate, selected, hasSameIndustryCodesObject) ||
            isBulkOverLimit
        );
    };

    useEffect(() => {
        if (isOpen && selected) {
            setIndustryCodesItem(selected);
        }

        if (!isOpen) {
            initValue();
        }
    }, [isOpen, selected]);

    const disabledIndustryCodes = useMemo(
        () => getUnavailableIndustryCodes(value ?? [], selected, codeType),
        [codeType, selected, value],
    );

    const chipItems = getIndustryCodesChipsData(industryCodesItem);

    return (
        <FilterConditionDialog
            isOpen={isOpen}
            onRequestClose={onClose}
            dataAutomation="industry-codes-modal"
            title={<I18n translatable={{ key: "si.lead_gen_filters.filters.industryCodes.modal.title" }} />}
            description={<I18n translatable={{ key: "si.lead_gen_filters.filters.industryCodes.modal.subtitle" }} />}
            onCancel={handleCancel}
            onApply={handleOnApply}
            isApplyDisabled={isApplyDisabled()}
            allowContentOverflow
        >
            <StyledModalContent>
                <StyledSegmentedControlContainer data-automation="industry-codes-input-mode-toggle">
                    <SegmentedControl
                        values={inputModeSegmentValues}
                        selectedIndex={inputModeIndex}
                        onChange={handleInputModeChange}
                    />
                </StyledSegmentedControlContainer>

                <StyledInputModeFrame>
                    <InclusionTechnologyRadioButtons
                        isExcludeAvailable={isExcludeAvailable}
                        selectedInclusion={industryCodesItem.inclusion}
                        onSelect={handleSetInclusion}
                    />

                    <StyledModalChipsSection>
                        {chipItems.length > 0 ? (
                            <ChipsGroup<IndustryCodesChipType>
                                chipItems={chipItems}
                                divider={operatorOptionMap.or}
                                onDelete={handleChipDelete}
                            />
                        ) : null}
                    </StyledModalChipsSection>

                    {inputModeIndex === INPUT_MODE_SINGLE_INDEX ? (
                        <StyledDropdownsFlexContainer>
                            <CodesDropdown
                                value={codeType}
                                onUpdate={setCodeType}
                                codeOptions={codeOptions}
                            />
                            <StyledCodesInputSection>
                                <AutocompleteCodes
                                    value={
                                        codeType === industryCodeDtoMap.naics
                                            ? filterValue.naicsCodes
                                            : filterValue.sicCodes
                                    }
                                    disabledCodes={disabledIndustryCodes}
                                    codes={codesFullList[codeType]}
                                    onUpdate={handleCodeUpdate}
                                />
                            </StyledCodesInputSection>
                        </StyledDropdownsFlexContainer>
                    ) : (
                        <StyledBulkModeContainer>
                            <CodesDropdown
                                value={codeType}
                                onUpdate={setCodeType}
                                codeOptions={codeOptions}
                            />
                            <StyledCodesInputSection>
                                <IndustryCodesBulkInput
                                    codeType={codeType}
                                    value={bulkRawValue}
                                    onChange={setBulkRawValue}
                                />
                            </StyledCodesInputSection>
                        </StyledBulkModeContainer>
                    )}
                </StyledInputModeFrame>
            </StyledModalContent>
        </FilterConditionDialog>
    );
};
